import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  changeSelectedQuantity,
  setSelectedSKU,
  setSelectedFirstProductVariant,
  setSelectedSecondSKU,
} from '../../../slices/pdp';
import { getVariantsWithPercentDiscount } from '../../../utils/variants';
import ProductVariantSelection from '../ProductVariantSelection';
import drupalPubSubService from '../../../services/drupalPubSub/drupalPubSub';
import {
  priceToString,
  getTargetProductWithAllVariantsBySKU,
} from '../../../utils';

import {
  SIZE_ATTRIBUTES,
  COLOR_ATTRIBUTES,
  HEIGHT_ATTRIBUTES,
  GENDER_ATTRIBUTES,
  QUANTITY_ATTRIBUTES,
} from '../../pdpV2/constants';
import useQueryParams from '../../../hooks/useQueryParams';
import useDynamicPdpDrupalSettings from '../../../hooks/useDynamicPdpDrupalSettings';
import { VARIANT_ATTRIBUTE_NAME_SIZE } from '../../pdpV2/VariantSelection/constants';

export const PRODUCT_TYPES_SINGULAR = {
  pillow: 'pillow',
  mattress: 'mattress',
  bedFrames: 'bed_frames',
  sheets: 'sheet',
  defaultProductName: 'product',
};

export const productTypeToSingularProductNameConverter = (productType) => {
  const productTypeToSingularProductNameMatching = {
    pillows: PRODUCT_TYPES_SINGULAR.pillow,
    mattresses: PRODUCT_TYPES_SINGULAR.mattress,
    bed_frames: PRODUCT_TYPES_SINGULAR.bedFrames,
    sheets: PRODUCT_TYPES_SINGULAR.sheets,
    defaultProductName: PRODUCT_TYPES_SINGULAR.defaultProductName,
  };
  return (
    productTypeToSingularProductNameMatching[productType] ||
    productTypeToSingularProductNameMatching.defaultProductName
  );
};

const ProductSelection = () => {
  const { product, aovs } = useDynamicPdpDrupalSettings();
  const { publish } = drupalPubSubService;

  const [selectedProduct, setSelectedProduct] = useState();

  const [productDiscountSavings, setProductDiscountSavings] =
    useState(undefined);

  const { formatMessage, formatNumber } = useIntl();

  const productType = selectedProduct?.productType;

  const dispatch = useDispatch();

  const { selectedSKU, selectedQty, selectedSecondSKU } = useSelector(
    (state) => state.pdp,
  );

  const hasAnItemSelected = useSelector(
    (state) => state.pdp.selectedBundlePdpAOVSKUs.length > 0,
  );

  const [productSize, productHeight, productColor, gender, productQuantity] =
    useQueryParams(['size', 'height', 'color', 'gender', 'quantity']);

  const productFromDrupalSettingsAsJSON = product
    ? JSON.stringify(product)
    : undefined;

  useEffect(() => {
    if (!hasAnItemSelected) {
      const productFromDrupalSettingsValue = productFromDrupalSettingsAsJSON
        ? JSON.parse(productFromDrupalSettingsAsJSON)
        : {};
      setSelectedProduct(productFromDrupalSettingsValue);
    }
  }, [hasAnItemSelected, productFromDrupalSettingsAsJSON]);

  const productVariantsWithDiscountedPrices = useMemo(() => {
    if (selectedProduct) {
      const discountPercentage =
        selectedProduct?.secondItemDiscount?.percentage;

      const newProductVariantsWithDiscounts = selectedProduct?.variants?.map(
        (variant) =>
          selectedSecondSKU && discountPercentage
            ? getVariantsWithPercentDiscount([variant], discountPercentage)?.[0]
            : variant,
      );
      return newProductVariantsWithDiscounts;
    }
    return [];
  }, [selectedSecondSKU, selectedProduct]);

  const masterVariant = productVariantsWithDiscountedPrices?.length
    ? productVariantsWithDiscountedPrices.find(
        (variant) => variant.masterVariant === true,
      )
    : null;

  const updateDiscountSavings = useCallback(
    (discount) => {
      if (discount.centAmount > 0) {
        setProductDiscountSavings(
          priceToString({
            price: discount,
            maximumFractionDigits: 0,
            formatNumber,
            formatMessage,
          }),
        );
      } else {
        setProductDiscountSavings(undefined);
      }
    },
    [formatNumber, formatMessage],
  );

  useEffect(() => {
    if (!selectedSKU && productVariantsWithDiscountedPrices?.length) {
      const variant = masterVariant || productVariantsWithDiscountedPrices[0];
      dispatch(setSelectedFirstProductVariant({ variant }));

      updateDiscountSavings(variant.discount);

      dispatch(setSelectedSKU({ selectedSKU: variant.sku, selectedQty: 1 }));
    }
  }, [
    dispatch,
    productVariantsWithDiscountedPrices,
    selectedSKU,
    formatNumber,
    formatMessage,
    updateDiscountSavings,
    masterVariant,
  ]);

  useEffect(() => {
    if (selectedSKU) {
      const previouslySelectedVariant =
        window?.drupalSettings?.dynamic_pdps?.products
          ?.flatMap((node) => node.data.product.variants)
          ?.find((variant) => variant.sku === selectedSKU);
      const selectedSKUExistsInTheCurrentVariants =
        productVariantsWithDiscountedPrices.find(
          (variant) => variant.sku === selectedSKU,
        );

      if (previouslySelectedVariant && !selectedSKUExistsInTheCurrentVariants) {
        const prevSizeKey = previouslySelectedVariant.attributes.find(
          (attribute) => attribute.name === VARIANT_ATTRIBUTE_NAME_SIZE,
        ).key;
        let newVariant = productVariantsWithDiscountedPrices.find(
          (variant) =>
            variant.attributes.find(
              (attribute) => attribute.name === VARIANT_ATTRIBUTE_NAME_SIZE,
            ).key === prevSizeKey,
        );

        if (!newVariant) {
          newVariant =
            productVariantsWithDiscountedPrices.find((variant) =>
              variant.attributes
                .find(
                  (attribute) => attribute.name === VARIANT_ATTRIBUTE_NAME_SIZE,
                )
                .key.includes(prevSizeKey),
            ) || masterVariant;
        }

        dispatch(setSelectedFirstProductVariant({ variant: newVariant }));

        if (newVariant?.discount) {
          updateDiscountSavings(newVariant.discount);
        }
        if (newVariant?.sku) {
          dispatch(
            setSelectedSKU({ selectedSKU: newVariant.sku, selectedQty: 1 }),
          );
        }
      }
    }
  }, [
    productVariantsWithDiscountedPrices,
    selectedSKU,
    masterVariant,
    dispatch,
    updateDiscountSavings,
  ]);

  const quantityIncrementHandler = useCallback(
    () => dispatch(changeSelectedQuantity({ selectedQty: selectedQty + 1 })),
    [dispatch, selectedQty],
  );
  const quantityDecrementHandler = useCallback(
    () => dispatch(changeSelectedQuantity({ selectedQty: selectedQty - 1 })),
    [dispatch, selectedQty],
  );

  const selectFirstSKUHandler = useCallback(
    (sku) => {
      const newSelectedFirstProductVariant =
        sku && product?.variants
          ? product.variants.find((variant) => variant.sku === sku)
          : undefined;

      if (newSelectedFirstProductVariant) {
        dispatch(
          setSelectedFirstProductVariant({
            variant: newSelectedFirstProductVariant,
          }),
        );

        updateDiscountSavings(newSelectedFirstProductVariant.discount);
      }

      dispatch(setSelectedSKU({ selectedSKU: sku }));
      publish({
        subscription: 'onVariantChange',
        parameters: newSelectedFirstProductVariant,
      });
    },
    [dispatch, product?.variants, publish, updateDiscountSavings],
  );

  const filterProductBasedOnQueryParams = (
    productVariant,
    parameter,
    filterBy,
  ) => ({
    ...productVariant,
    attributes:
      productVariant.attributes.filter(
        (attribute) =>
          filterBy.includes(attribute.name) &&
          (attribute.key.toLowerCase() === parameter.toLowerCase() ||
            attribute.label.toLowerCase().replace(/ +/i, '_') ===
              parameter.toLowerCase() ||
            attribute.key
              .toLowerCase()
              .split('/')
              .includes(parameter.toLowerCase()) ||
            (attribute.key.toLowerCase().match(/\d+/) &&
              attribute.key.toLowerCase().match(/\d+/)[0] ===
                parameter.toLowerCase())),
      ).length > 0 && productVariant.attributes,
  });

  useEffect(() => {
    let selectedVariant = null;
    const { variants } = product || {};
    if (variants) {
      if (productSize) {
        selectedVariant = variants
          .map((element) =>
            filterProductBasedOnQueryParams(
              element,
              productSize,
              SIZE_ATTRIBUTES,
            ),
          )
          .filter((variant) => variant.attributes.length > 0);
        if (productHeight) {
          selectedVariant = selectedVariant
            .map((element) =>
              filterProductBasedOnQueryParams(
                element,
                productHeight,
                HEIGHT_ATTRIBUTES,
              ),
            )
            .filter((variant) => variant.attributes.length > 0);
        }
        if (productColor) {
          selectedVariant = selectedVariant
            .map((element) =>
              filterProductBasedOnQueryParams(
                element,
                productColor,
                COLOR_ATTRIBUTES,
              ),
            )
            .filter((variant) => variant.attributes.length > 0);
        }
        if (gender) {
          selectedVariant = selectedVariant
            .map((element) =>
              filterProductBasedOnQueryParams(
                element,
                gender,
                GENDER_ATTRIBUTES,
              ),
            )
            .filter((variant) => variant.attributes.length > 0);
        }
      } else if (productColor) {
        selectedVariant = variants
          .map((element) =>
            filterProductBasedOnQueryParams(
              element,
              productColor,
              COLOR_ATTRIBUTES,
            ),
          )
          .filter((variant) => variant.attributes.length > 0);
      } else if (productQuantity) {
        selectedVariant = variants
          .map((element) =>
            filterProductBasedOnQueryParams(
              element,
              productQuantity,
              QUANTITY_ATTRIBUTES,
            ),
          )
          .filter((variant) => variant.attributes.length > 0);
      }
    }
    if (selectedVariant) {
      const sku = selectedVariant[0]?.sku;
      selectFirstSKUHandler(sku);
    }
    // Disabled eslint-disable-next-line, react-hooks/exhaustive-deps
    // since it is required to run only once which is when the component is mounting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFromDrupalSettingsAsJSON]);

  const selectSecondSKUHandler = (sku) => {
    dispatch(setSelectedSecondSKU(sku));
  };

  useEffect(() => {
    if (hasAnItemSelected) {
      const bundleAOVsV1 = aovs?.find(
        ({ targetSku, bundles }) =>
          targetSku === selectedSKU && bundles?.showOnPdp,
      );
      if (bundleAOVsV1) {
        const { bundles } = bundleAOVsV1;
        const { items } = bundles;

        const bundleTargetProduct = getTargetProductWithAllVariantsBySKU(
          items,
          selectedSKU,
        );
        setSelectedProduct(bundleTargetProduct);
      } else {
        const bundleAOVsV2 = aovs?.find(
          ({ targetSku, bundles }) =>
            targetSku === selectedSKU && bundles?.showOnPdpV2,
        );
        const bundleTargetProduct = getTargetProductWithAllVariantsBySKU(
          bundleAOVsV2?.bundles?.items,
          selectedSKU,
        );
        setSelectedProduct(bundleTargetProduct);
      }
    }
  }, [aovs, hasAnItemSelected, selectedSKU]);

  if (!product) return <div />;

  return selectedProduct && productVariantsWithDiscountedPrices?.length ? (
    <>
      <ProductVariantSelection
        product={{
          name: selectedProduct?.name,
          key: selectedProduct?.key,
          secondItemDiscount: {
            percentage: selectedProduct?.secondItemDiscount?.percentage,
            preAddedMessage:
              selectedProduct?.secondItemDiscount?.preAddedMessage,
            postAddedMessage:
              selectedProduct?.secondItemDiscount?.postAddedMessage,
          },
          variants: productVariantsWithDiscountedPrices,
          productTypeSingular:
            productTypeToSingularProductNameConverter(productType),
          attributesExtraInfo: selectedProduct?.attribute_links,
        }}
        selectedFirstSKU={selectedSKU}
        onSelectFirstSKU={selectFirstSKUHandler}
        selectedFirstSKUQuantity={selectedQty}
        selectedSecondSKU={selectedSecondSKU}
        onSelectSecondSKU={selectSecondSKUHandler}
        onFirstProductQuantityIncrement={quantityIncrementHandler}
        onFirstProductQuantityDecrement={quantityDecrementHandler}
        selectedFirstSKUDiscount={productDiscountSavings}
      />
    </>
  ) : null;
};

export default ProductSelection;
